import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { EllipsisText, JsonBlock } from '../Assessments/shared';
import { RegistrySeedResult } from './actionTypes';
import { fetchRegistryCoverage, listBlueprintFamilies } from './api';
import { readableError } from './errors';
import { asNumber, useModuleFilters } from './filters';
import { JobsStrip } from './Jobs';
import {
  ConflictNotice,
  JobOutcomePanel,
  JobStallNotice,
  useJobRun,
} from './JobRun';
import {
  BlueprintCoverageRow,
  BlueprintFamilyOption,
  RegistryAssetRow,
  RegistryCoverageResponse,
} from './types';

/**
 * The registry seeder, run from the screen that shows what it would fix.
 *
 * Always planned before it is applied, and the screen never writes around the
 * seeder: the same planner produces the dry run and the write, so what an
 * admin approves is what runs.
 */
const SeederDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}> = ({ open, onClose, onApplied }) => {
  const seed = useJobRun();
  const [force, setForce] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (!open) {
      seed.reset();
      setApplied(false);
      setForce(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const result = (
    seed.job?.status === 'SUCCEEDED' ? seed.job.result : null
  ) as RegistrySeedResult | null;
  const planned = Boolean(result) && result?.applied !== true;

  const run = async (apply: boolean) => {
    const finished = await seed.run('registry.seed', { apply, force });
    if (apply && finished?.status === 'SUCCEEDED') {
      setApplied(true);
      onApplied();
    }
  };

  const consequence = result
    ? `Applying writes ${result.link ?? 0} new PM link${
        (result.link ?? 0) === 1 ? '' : 's'
      } and changes ${result.update ?? 0} existing one${
        (result.update ?? 0) === 1 ? '' : 's'
      } on the registry. ${
        force
          ? 'Force is on: links a human set by hand are overwritten too.'
          : 'Links a human set by hand are left alone.'
      }`
    : '';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={860}
      title="Seed registry links"
      destroyOnHidden
    >
      <div className="assessment-detail-stack">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="The plan is a dry run and writes nothing"
          description="It reports what the seeder would link, keep, change and refuse. Nothing is written to the registry until the plan is applied."
        />
        <Card size="small" title="Plan">
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space>
              <Button
                type="primary"
                loading={seed.running}
                onClick={() => run(false)}
              >
                Plan the seeding
              </Button>
              <Button
                onClick={() => setForce(!force)}
                danger={force}
                aria-pressed={force}
              >
                {force
                  ? 'Force is on: hand-set links will be overwritten'
                  : 'Leave hand-set links alone'}
              </Button>
            </Space>
          </Space>
        </Card>

        <Card size="small" title="Recent seeder runs">
          <JobsStrip target_kind="registry" target_id={0} />
        </Card>

        <ConflictNotice job={seed.conflict} />
        {seed.error && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={seed.error}
          />
        )}
        <JobOutcomePanel
          job={seed.job}
          successTitle={
            applied ? 'The registry links were written' : 'Plan ready'
          }
          elapsedMs={seed.elapsedMs}
        />
        <JobStallNotice stall={seed.stall} />

        {result && (
          <>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="Plans">
                {result.plans ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="New links">
                {result.link ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Unchanged">
                {result.keep ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Changed">
                {result.update ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Refused">
                {result.refused ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="No tier matched">
                {result.no_tier_matched?.length ?? 0}
              </Descriptions.Item>
            </Descriptions>
            {result.bond_families && (
              <Card size="small" title="Bond families">
                <JsonBlock compact value={result.bond_families} />
              </Card>
            )}
          </>
        )}

        {planned && !applied && (
          <Card size="small" title="Apply the plan">
            <Alert
              className="assessment-callout"
              type="warning"
              showIcon
              title={consequence}
            />
            <Popconfirm
              title="Write these registry links?"
              description={consequence}
              okText="Write the links"
              cancelText="Not yet"
              onConfirm={() => run(true)}
            >
              <Button type="primary" danger={force} loading={seed.running}>
                Apply the plan
              </Button>
            </Popconfirm>
          </Card>
        )}
      </div>
    </Drawer>
  );
};

const refusalTags = (codes: string[]) =>
  codes.length ? (
    <Space size={[4, 4]} wrap className="assessment-tag-group">
      {codes.map(code => (
        <Tag key={code} color="red">
          {code}
        </Tag>
      ))}
    </Space>
  ) : (
    <Tag color="green">OK</Tag>
  );

/** Case-insensitive containment over the strings a row is findable by. */
const matches = (
  haystack: Array<string | null | undefined>,
  needle: string,
) => {
  const wanted = needle.trim().toLowerCase();
  if (!wanted) return true;
  return haystack.some(value =>
    String(value || '')
      .toLowerCase()
      .includes(wanted),
  );
};

const RegistryCoverage: React.FC = () => {
  const [response, setResponse] = useState<RegistryCoverageResponse | null>(
    null,
  );
  const [blueprints, setBlueprints] = useState<BlueprintFamilyOption[]>([]);
  // In the address bar, so the Problems screen's remedies can hand this
  // screen the rows they are about instead of dropping an admin into an
  // unfiltered 88-row table and leaving them to find six of them.
  const [filters, setFilters] = useModuleFilters('reg', {
    q: '',
    blueprint: '',
    unmapped: '',
  });
  const blueprintId = asNumber(filters.blueprint);
  const search = filters.q;
  const unmappedOnly = filters.unmapped === '1';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [seederOpen, setSeederOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setResponse(
        await fetchRegistryCoverage(
          blueprintId ? { blueprint: blueprintId } : undefined,
        ),
      );
    } catch (requestError) {
      setError(readableError(requestError, { what: 'Registry coverage' }));
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [blueprintId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // The picker reuses the existing blueprint family list; coverage still
    // works with a manually typed id if this read fails.
    listBlueprintFamilies()
      .then(setBlueprints)
      .catch(() => setBlueprints([]));
  }, []);

  const blueprintMode = Boolean(blueprintId);

  const registryRows = useMemo(() => {
    if (!response || response.mode === 'blueprint') return [];
    return (response.results as RegistryAssetRow[])
      .filter(row => !unmappedOnly || !row.pm_asset)
      .filter(row =>
        matches(
          [
            row.canonical_code,
            row.name,
            row.role,
            row.pm_asset?.ticker,
            row.pm_asset?.name,
            // The PM asset id, because that is what every screen that links
            // HERE names ("6 asset(s) with no registry concept: 2377, 2378").
            // Searching for one of those ids used to match nothing.
            row.pm_asset?.id == null ? undefined : String(row.pm_asset.id),
            ...(row.refusals || []),
          ],
          search,
        ),
      );
  }, [response, search, unmappedOnly]);

  const blueprintRows = useMemo(() => {
    if (!response || response.mode !== 'blueprint') return [];
    return (response.results as BlueprintCoverageRow[]).filter(row =>
      matches(
        [
          row.label,
          row.canonical_code,
          row.role,
          row.assessment_column,
          row.problem,
        ],
        search,
      ),
    );
  }, [response, search]);

  const registryColumns: any[] = [
    {
      title: 'Registry asset',
      fixed: 'left',
      width: 220,
      render: (_: unknown, row: RegistryAssetRow) => (
        <Space direction="vertical" size={0}>
          <EllipsisText code value={row.canonical_code} />
          <Tooltip title={row.name} placement="topLeft">
            <span className="assessment-ellipsis">{row.name}</span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Class',
      width: 90,
      render: (_: unknown, row: RegistryAssetRow) => (
        <Tag>{row.asset_class}</Tag>
      ),
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      width: 110,
      render: (value: string) => <EllipsisText value={value} />,
    },
    {
      title: 'PM instrument',
      width: 220,
      render: (_: unknown, row: RegistryAssetRow) =>
        row.pm_asset ? (
          <Space direction="vertical" size={0}>
            <EllipsisText
              code
              value={`#${row.pm_asset.id} ${row.pm_asset.ticker || ''}`.trim()}
            />
            <Tooltip title={row.pm_asset.name} placement="topLeft">
              <span className="assessment-ellipsis">{row.pm_asset.name}</span>
            </Tooltip>
          </Space>
        ) : (
          <Tag color="red">UNLINKED</Tag>
        ),
    },
    {
      title: 'Relation',
      width: 110,
      render: (_: unknown, row: RegistryAssetRow) => (
        <Space direction="vertical" size={2}>
          <Tag color={row.pm_relation === 'proxy' ? 'purple' : 'default'}>
            {(row.pm_relation || 'identity').toUpperCase()}
          </Tag>
          {row.pm_note && (
            <Tooltip title={row.pm_note}>
              <Typography.Text type="secondary">note</Typography.Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Conviction role',
      dataIndex: 'role',
      width: 130,
      render: (value: string) =>
        value ? (
          <Typography.Text code>{value}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Refusals',
      width: 240,
      render: (_: unknown, row: RegistryAssetRow) =>
        refusalTags(row.refusals || []),
    },
    {
      title: 'Collisions',
      width: 200,
      render: (_: unknown, row: RegistryAssetRow) =>
        row.collisions?.length ? (
          <Space size={[4, 4]} wrap className="assessment-tag-group">
            {row.collisions.map(code => (
              <Tag key={code} color="orange">
                {code}
              </Tag>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  const blueprintColumns: any[] = [
    {
      title: 'PM instrument',
      fixed: 'left',
      width: 240,
      render: (_: unknown, row: BlueprintCoverageRow) => (
        <Space direction="vertical" size={0}>
          <EllipsisText code value={`#${row.pm_id}`} />
          <Tooltip title={row.label} placement="topLeft">
            <span className="assessment-ellipsis">{row.label}</span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Registry asset',
      dataIndex: 'canonical_code',
      width: 180,
      render: (value: string) => <EllipsisText code value={value} />,
    },
    {
      title: 'Relation',
      dataIndex: 'relation',
      width: 110,
      render: (value: string) =>
        value ? (
          <Tag color={value === 'proxy' ? 'purple' : 'default'}>
            {value.toUpperCase()}
          </Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Conviction role',
      dataIndex: 'role',
      width: 130,
      render: (value: string) =>
        value ? (
          <Typography.Text code>{value}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Assessment column',
      dataIndex: 'assessment_column',
      width: 160,
      render: (value: string | null) =>
        value ? (
          <Typography.Text code>{value}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Refusal',
      width: 320,
      render: (_: unknown, row: BlueprintCoverageRow) =>
        row.problem ? (
          <Tag color="red">{row.problem}</Tag>
        ) : (
          <Tag color="green">OK</Tag>
        ),
    },
  ];

  const scenarioCode = response?.scenario_code;

  return (
    <Card
      title="Registry coverage"
      extra={
        <Space>
          <Button type="primary" onClick={() => setSeederOpen(true)}>
            Run seeder
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </Space>
      }
    >
      <Alert
        className="assessment-callout"
        showIcon
        type="info"
        title="Asset resolution chain"
        description="Without a blueprint, every tracked registry asset is graded against PM's live static eligibility law (registry asset, PM instrument, relation, conviction role, refusal reasons). Selecting a blueprint resolves its latest version's template session into the asset, PM instrument, role, and assessment column chain."
      />
      <Space className="assessment-filter-row" wrap>
        <Select
          allowClear
          showSearch
          aria-label="Blueprint scope"
          placeholder="Whole registry (select a blueprint to scope)"
          style={{ width: 340 }}
          value={blueprintId}
          onChange={value =>
            setFilters({ blueprint: value ? String(value) : '' })
          }
          optionFilterProp="label"
          options={blueprints.map(blueprint => ({
            value: blueprint.id,
            label: `${blueprint.name} (#${blueprint.id})`,
          }))}
        />
        <Input.Search
          allowClear
          placeholder="Search code, name, role, refusal, or PM asset id"
          style={{ width: 360 }}
          defaultValue={search}
          onSearch={value => setFilters({ q: value })}
        />
        <Select
          allowClear
          aria-label="Mapping state"
          placeholder="Mapped and unmapped"
          style={{ width: 240 }}
          value={unmappedOnly ? '1' : undefined}
          onChange={value => setFilters({ unmapped: value || '' })}
          options={[{ value: '1', label: 'Only concepts with no PM asset' }]}
        />
        {blueprintMode && scenarioCode && (
          <Link
            to={`/dashboard/assessments?scenario=${encodeURIComponent(
              scenarioCode,
            )}`}
          >
            <Button size="small">Open in Assessments admin</Button>
          </Link>
        )}
      </Space>
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      {blueprintMode ? (
        <Table
          rowKey="pm_id"
          className="assessment-table"
          tableLayout="fixed"
          loading={loading}
          dataSource={blueprintRows}
          columns={blueprintColumns}
          scroll={{ x: 1140 }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
            showTotal: total => `${total} role${total === 1 ? '' : 's'}`,
          }}
        />
      ) : (
        <Table
          rowKey="canonical_code"
          className="assessment-table"
          tableLayout="fixed"
          loading={loading}
          dataSource={registryRows}
          columns={registryColumns}
          scroll={{ x: 1420 }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
            showTotal: total =>
              `${total} registry concept${total === 1 ? '' : 's'}`,
          }}
        />
      )}
      <SeederDrawer
        open={seederOpen}
        onClose={() => setSeederOpen(false)}
        onApplied={() => {
          void load();
        }}
      />
    </Card>
  );
};

export default RegistryCoverage;
